// Výběr varianty přepínači místo rozbalovacího seznamu (mock schválený Tomášem
// 2026-09-24, `design_handoff_vyber_velikosti/`): „Velikost: L" + řada tlačítek M L XL XXL.
// Na ostré 14 variantních produktů, všechno oblečení a obuv s jediným parametrem.
//
// Nativní prvek zůstává ZDROJEM PRAVDY a jen se skryje. Klik nastaví
// `select.value` (nebo `radio.checked`) a pošle `change` — default JS má na
// tomtéž prvku jQuery `.bind('change')` (`shoptet.variantsSimple.handler`)
// a sám přepne cenu, dostupnost, `priceId`, obrázek, tracking i tlačítko košíku.
// Nic z toho tu nepočítáme.
//
// `#simple-variants` má dvě podoby podle toho, jestli mají varianty vlastní fotku:
//   • `<select name="priceId">` — volby „Velikost: M - Skladem >5 ks (479 Kč)";
//   • radia v `label.advanced-parameter.has-image` — obrázek varianty, hodnota
//     jen v `title` („Velikost: 42"). Bez nás = řada stejných fotek bez čísla.
// Rozdělené parametry (`.hidden-split-parameter`, barva × velikost) se neobsluhují,
// zůstává nativní výběr (na ostré žádný produkt je nemá).
//
// Vyprodaná varianta: přeškrtnutá, nejde zvolit, pod řadou vyjmenovaná. Nízká
// zásoba (≤ 4 ks, pásma `stockLevel`): odznak v rohu a věta po volbě. Cena
// v tlačítku jen tam, kde se ceny variant liší. „Do košíku" bez volby: default
// JS přidá `body.variant-not-chosen` a doscrolluje na `.variant-not-chosen-anchor`
// (obal řady) — řada zčervená a ukáže „Vyberte velikost".
import L from '../locale/cs/variantPicker.js';
import { money } from './money.js';
import { lowPieces, parseVariantText, pricesDiffer, sortOptions } from './variantPickerModel.js';

const LOW_MAX = 4;

function node(tag, cls, txt) {
  const el = document.createElement(tag);
  if (cls) el.className = cls;
  if (txt != null) el.textContent = txt;
  return el;
}

function text(el) {
  return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

// Nativní skupina dostupnosti varianty (`span.choose-variant.<index>` se štítkem).
// productDetail.js ji stěhuje z tabulky formu do pilulky v meta řádku.
function stockGroup(index) {
  const idx = String(index);
  const groups = document.querySelectorAll('.choose-variant');
  for (let i = 0; i < groups.length; i += 1) {
    if (groups[i].classList.contains(idx) && groups[i].querySelector('.availability-label')) return groups[i];
  }
  return null;
}

// Volby z nativního markupu: `{ el, value, label, param, stock, sold, low, price }`.
function readOptions(host, select) {
  const opts = [];
  const add = function (el, raw) {
    const parsed = parseVariantText(raw, L.optionRe);
    if (!parsed) return;
    const sold = el.getAttribute('data-disable-button') === '1';
    const group = stockGroup(el.getAttribute('data-index'));
    opts.push({
      el: el,
      value: el.value,
      label: parsed.value,
      param: parsed.param,
      stock: text(group) || parsed.stock,
      sold: sold,
      low: sold ? null : lowPieces(text(group && group.querySelector('.availability-amount')), LOW_MAX),
      price: Number(el.getAttribute('data-customerprice')),
    });
  };
  if (select) {
    [...select.options].forEach((o) => {
      if (o.value) add(o, o.textContent);
    });
  } else {
    host.querySelectorAll('input[type="radio"][name="priceId"]').forEach((r) => {
      if (!r.value) return;
      const lab = r.closest('label');
      const inner = lab && lab.querySelector('.advanced-parameter-inner');
      add(r, inner && (inner.getAttribute('title') || inner.getAttribute('data-original-title')));
    });
  }
  return opts;
}

export function initVariantPicker() {
  const host = document.getElementById('simple-variants');
  if (!host || !document.body.classList.contains('type-detail')) return;
  const list0 = host.parentNode;
  if (!list0 || list0.querySelector('.bs-vp')) return; // idempotence
  const select = host.querySelector('select[name="priceId"]');
  const opts = sortOptions(readOptions(host, select));
  // Volby bez čitelného textu (jiný formát, jiný parametr) = nechat nativní výběr
  const expected = select
    ? [...select.options].filter((o) => o.value).length
    : host.querySelectorAll('input[type="radio"][name="priceId"]:not([value=""])').length;
  if (!opts.length || opts.length !== expected) return;
  const param = opts[0].param;
  const priced = pricesDiffer(opts);

  const root = node('div', 'bs-vp');
  const head = node('div', 'bs-vp__head');
  head.appendChild(node('span', 'bs-vp__param', param + ':'));
  const valueEl = node('strong', 'bs-vp__value');
  head.appendChild(valueEl);
  root.appendChild(head);
  const list = node('div', 'bs-vp__list');
  list.setAttribute('role', 'radiogroup');
  list.setAttribute('aria-label', param);
  root.appendChild(list);
  const note = node('div', 'bs-vp__note');
  note.setAttribute('aria-live', 'polite');
  root.appendChild(note);

  opts.forEach((o) => {
    const b = node('button', 'bs-vp__opt');
    b.type = 'button';
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', 'false');
    b.dataset.value = o.value;
    b.appendChild(node('span', 'bs-vp__label', o.label));
    const price = priced ? money(o.price) : null;
    if (price) {
      b.classList.add('bs-vp__opt--priced');
      b.appendChild(node('span', 'bs-vp__price', price));
    }
    if (o.sold) {
      b.setAttribute('aria-disabled', 'true');
      b.title = L.soldTitle;
    } else if (o.low) {
      b.appendChild(node('span', 'bs-vp__low', L.pieces(o.low)));
    }
    o.button = b;
    list.appendChild(b);
  });

  const current = () => opts.find((o) => (select ? select.value === o.value : o.el.checked)) || null;
  const soldLabels = opts.filter((o) => o.sold).map((o) => o.label);
  let error = false;

  function setNote(words, mod) {
    const cls = 'bs-vp__note' + (mod ? ' bs-vp__note--' + mod : '');
    if (note.className !== cls) note.className = cls;
    if (note.textContent !== words) note.textContent = words;
  }

  function paint() {
    const cur = current();
    opts.forEach((o) => o.button.setAttribute('aria-checked', String(o === cur)));
    valueEl.textContent = cur ? cur.label : '';
    if (cur) error = false;
    root.classList.toggle('bs-vp--error', error);
    if (error) setNote(L.choose(param), '');
    else if (cur && cur.low) setNote(L.lastPieces(cur.low), 'low');
    // dostupnost zvolené varianty z nativní skupiny (přepíná ji default JS)
    else if (cur) setNote(text(stockGroup(cur.el.getAttribute('data-index'))) || cur.stock, cur.sold ? 'sold' : 'stock');
    else if (soldLabels.length) setNote(L.soldList(soldLabels.join(', ')), 'sold');
    else setNote('', '');
  }

  list.addEventListener('click', function (e) {
    const b = e.target.closest('.bs-vp__opt');
    if (!b || b.getAttribute('aria-disabled') === 'true') return;
    const o = opts.find((x) => x.button === b);
    if (!o || o === current()) return;
    if (select) select.value = o.value;
    else o.el.checked = true;
    o.el.dispatchEvent(new Event('change', { bubbles: true }));
    paint();
  });
  // obnova hodnoty prohlížečem (zpět v historii), nativní reset parametrů
  host.addEventListener('change', paint);

  // „Do košíku" bez volby (i ze sticky lišty — kliká na nativní tlačítko)
  new MutationObserver(function () {
    const missing = document.body.classList.contains('variant-not-chosen') && !current();
    if (missing && !error) {
      error = true;
      root.classList.remove('bs-vp--error');
      void root.offsetWidth; // restart animace zatřesení
      paint();
    }
  }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

  host.classList.add('bs-vp-native');
  list0.classList.add('bs-vp-host');
  host.insertAdjacentElement('beforebegin', root);
  paint();
}
